import type { YDocument } from '@/modules/yjs/hooks'
import { useYValue } from '@/modules/yjs/hooks/useYValue'
import { getValueByYPath, toYStructure } from '@/shared/yUtils'
import { Block } from '@ttab/elephant-api/newsdoc'
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@ttab/elephant-ui'
import { MessageSquarePlusIcon } from '@ttab/elephant-ui/icons'
import { cn } from '@ttab/elephant-ui/utils'
import type * as Y from 'yjs'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'

type NoteRole = 'internal' | 'public'

export const AddNote = ({ ydoc, text = '', role }: {
  ydoc: YDocument<Y.Map<unknown>>
  text?: string
  role?: NoteRole
}): JSX.Element => {
  const [, setNotes] = useYValue<Block[] | undefined>(ydoc.ele, 'meta.core/note')
  const { t } = useTranslation('metaSheet')

  const handleClick = (role: NoteRole) => {
    const newNote = Block.create({
      id: crypto.randomUUID(),
      type: 'core/note',
      data: {
        text: ''
      },
      role
    })

    // Append to the live array. Writing a whole new core/note value replaces
    // every existing note with a freshly built Y structure, which throws away
    // what anyone else is typing at the time and undoes their removals.
    const [yNotes] = getValueByYPath<Y.Array<unknown>>(ydoc.ele, 'meta.core/note', true)

    if (yNotes) {
      yNotes.push([toYStructure(newNote)])
      return
    }

    // Nothing to preserve when the document has no notes yet.
    setNotes([newNote])
  }

  if (role) {
    return (
      <Button
        variant='ghost'
        onClick={() => handleClick(role)}
        className='hover:bg-gray-200 dark:hover:bg-table-focused rounded-md px-1 flex flex-row items-center justify-center gap-1 h-9 p-0 m-0 min-w-9'
      >
        <MessageSquarePlusIcon size={18} strokeWidth={1.75} />
        {text && <span className='text-muted-foreground'>{text}</span>}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn('flex flex-row items-center justify-center gap-1 h-9 p-0 m-0 min-w-9 hover:bg-gray-200 dark:hover:bg-table-focused rounded-md px-1',
          text
            ? 'text-muted-foreground text-xs border rounded-md'
            : '')}
      >
        <MessageSquarePlusIcon size={18} strokeWidth={1.75} />
        {text && <span className='text-muted-foreground'>{text}</span>}
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => handleClick('internal')}>
          {t('dropDownMenuItems.internalNote')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleClick('public')}>
          {t('dropDownMenuItems.infoToCustomer')}
        </DropdownMenuItem>
      </DropdownMenuContent>

    </DropdownMenu>
  )
}
